import {
	Button,
	ButtonGroup,
	Menu,
	MenuItem,
	Popover,
} from '@blueprintjs/core';
import { observer } from 'mobx-react-lite';
import React, { useEffect, useMemo, useState } from 'react';

interface TextFontVariantProps {
	elements: any[];
	store: any;
	activeFont: string;
	customFonts?: Record<string, any[]>;
}

interface FontVariant {
	weight: string;
	style: string;
	fontFamily: string;
}

const ALIGN_OPTIONS = ['left', 'center', 'right', 'justify'] as const;

export const TextFontVariant: React.FC<TextFontVariantProps> = observer(
	({ elements, store, activeFont, customFonts }) => {
		const firstElement = elements[0];
		const [currentFontList, setCurrentFontList] = useState<FontVariant[]>([]);
		const [isCustomFontSelected, setIsCustomFontsSelected] = useState(false);

		useEffect(() => {
			if (customFonts) {
				const customFontListTemp: FontVariant[] = [];

				// I need to reset the flag exactly in that place
				setIsCustomFontsSelected(false);

				for (const [fontKey, fonts] of Object.entries(customFonts)) {
					if (activeFont.includes(fontKey) || fontKey === activeFont) {
						fonts.forEach((font) => {
							customFontListTemp.push({
								weight: font.cssWeight,
								style: font.style,
								fontFamily: fontKey,
							});
						});
						setIsCustomFontsSelected(true);
					}
				}

				setCurrentFontList(customFontListTemp);
			} else {
				setIsCustomFontsSelected(false);
			}
		}, [activeFont, customFonts]);

		/* const handleClickCustomFont = (fontFamily) => {
			store.history.transaction(() => {
				elements.forEach((element) => {
					element.set({
						fontFamily,
					});
				});
			});
		}; */

		const handleChangeStyle = (itemId: string) => {
			const isItalic = firstElement.fontStyle === 'italic';
			const isOblique = firstElement.fontStyle === 'oblique';
			const isBold =
				firstElement.fontWeight === 'bold' || firstElement.fontWeight === '700';

			switch (itemId) {
			case 'bold':
				store.history.transaction(() => {
					elements.forEach((element) => {
						if (isBold) {
							element.set({ fontWeight: 'normal' });
						} else {
							element.set({ fontWeight: 'bold' });
						}
					});
				});
				break;
			case 'italic':
				store.history.transaction(() => {
					elements.forEach((element) => {
						if (isItalic) {
							element.set({ fontStyle: 'normal' });
						} else {
							element.set({ fontStyle: 'italic' });
						}
					});
				});
				break;
			case 'oblique':
				store.history.transaction(() => {
					elements.forEach((element) => {
						if (isOblique) {
							element.set({ fontStyle: 'normal' });
						} else {
							element.set({ fontStyle: 'italic' });
						}
					});
				});
				break;
			default:
				break;
			}
		};

		const stylesMenu = useMemo(() => {
			/* if (customFonts && isCustomFontSelected) {
				return (
					<Menu>
						{currentFontList.map((font, index) => {
							return (
								font.weight === 'bold' ? (
									<MenuItem
										key={index}
										active={firstElement.fontFamily === font.fontFamily && firstElement.fontWeight === 'bold'}
										icon="bold"
										text={'Bold'}
										onClick={() => handleClickCustomFont(font.fontFamily)}
									/>
								) : (
									font.style === 'italic' ? <MenuItem
										key={index}
										active={firstElement.fontFamily === font.fontFamily && firstElement.fontStyle === 'italic'}
										icon="italic"
										text={'Italic'}
										onClick={() => handleClickCustomFont(font.fontFamily)}
									/>
									: false
								)
							);
						})}
					</Menu>
				);
			} else {
				return (
					<Menu>
						<MenuItem
							active={
								firstElement.fontWeight === 'bold' ||
								firstElement.fontWeight === '700'
							}
							icon="bold"
							text="Bold"
							onClick={() => handleChangeStyle('bold')}
						/>
						<MenuItem
							active={firstElement.fontStyle === 'italic'}
							icon="italic"
							text="Italic"
							onClick={() => handleChangeStyle('italic')}
						/>
					</Menu>
				);
			} */

			return (
				<Menu>
					<MenuItem
						active={
							firstElement.fontWeight === 'bold' ||
							firstElement.fontWeight === '700'
						}
						icon="bold"
						text="Bold"
						onClick={() => handleChangeStyle('bold')}
					/>
					<MenuItem
						active={firstElement.fontStyle === 'italic'}
						icon="italic"
						text="Italic"
						onClick={() => handleChangeStyle('italic')}
					/>
				</Menu>
			);
		}, [
			isCustomFontSelected,
			currentFontList,
			customFonts,
			firstElement.fontStyle,
			firstElement.fontWeight,
		]);

		return (
			<ButtonGroup>
				<Popover content={stylesMenu} fill placement="bottom">
					<Button
						alignText="left"
						fill
						rightIcon="caret-down"
						text="Font Style..."
					/>
				</Popover>
				<Button
					minimal
					icon={`align-${firstElement.align}` as any}
					onClick={() => {
						const currentIndex = ALIGN_OPTIONS.indexOf(firstElement.align);
						const nextIndex =
							(currentIndex + 1 + ALIGN_OPTIONS.length) % ALIGN_OPTIONS.length;
						const next = ALIGN_OPTIONS[nextIndex];
						store.history.transaction(() => {
							elements.forEach((element) => {
								element.set({
									align: next,
								});
							});
						});
					}}
				/>
				<Button
					minimal
					active={firstElement.textDecoration.indexOf('underline') >= 0}
					icon="underline"
					onClick={() => {
						let styles = firstElement.textDecoration.split(' ');
						if (styles.indexOf('underline') >= 0) {
							styles = styles.filter((s: string) => s !== 'underline');
						} else {
							styles.push('underline');
						}
						store.history.transaction(() => {
							elements.forEach((element) => {
								element.set({ textDecoration: styles.join(' ') });
							});
						});
					}}
				/>
				<Button
					minimal
					active={firstElement.textDecoration.indexOf('line-through') >= 0}
					icon="strikethrough"
					onClick={() => {
						let styles = firstElement.textDecoration.split(' ');
						if (styles.indexOf('line-through') >= 0) {
							styles = styles.filter((s: string) => s !== 'line-through');
						} else {
							styles.push('line-through');
						}
						store.history.transaction(() => {
							elements.forEach((element) => {
								element.set({ textDecoration: styles.join(' ') });
							});
						});
					}}
				/>
			</ButtonGroup>
		);
	},
);

export default TextFontVariant;
